/**
 * Helpers for the SME IPO list page (/sme-ipos). Builds one row per SME issue:
 *
 * - GET /api/core/ipos/sme -- the SME IPO list itself: company, platform,
 *   status, price, issue and lot size, open/close/listing dates, GMP, and the
 *   catalog id for the detail page when one exists
 * - GET /api/market/ipos/sme/upcoming (NSE rows, series "SME") -- symbol,
 *   price band and exact bidding dates for issues NSE has scheduled
 * - GET /api/market/gmp/sme (board "SME") -- allotment date, day-range GMP
 *   and subscription from the grey market source
 * - GET /api/market/subscription/sme -- live subscription multiple by symbol
 */
import { deriveBoard, parsePriceRange, toIsoDate } from './ipoCatalogAdapters';
import { toGmpSlug } from './gmpDataMapper';
import { mapApiItemsToBoardRows } from './gmpBoard';
import { todayIso } from './ipoCalendar';

export const SME_STATUS = {
  closing_today: { label: 'Closing Today', badge: 'bg-rose-50 text-rose-600 border-rose-200' },
  open: { label: 'Open', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  upcoming: { label: 'Upcoming', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  closed: { label: 'Closed', badge: 'bg-slate-100 text-slate-600 border-slate-200' },
  allotted: { label: 'Allotted', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  listed: { label: 'Listed', badge: 'bg-violet-50 text-violet-700 border-violet-200' },
};

const CLOSED_STATUSES = ['closed', 'allotted', 'listed'];

export const SME_TABS = [
  { key: 'all', label: 'All SME IPOs', shortLabel: 'All', test: () => true },
  { key: 'open', label: 'Open Now', shortLabel: 'Open', test: (row) => row.status === 'open' || row.status === 'closing_today' },
  { key: 'upcoming', label: 'Upcoming', shortLabel: 'Upcoming', test: (row) => row.status === 'upcoming' },
  { key: 'closed', label: 'Closed & Listed', shortLabel: 'Closed', test: (row) => CLOSED_STATUSES.includes(row.status) },
];

export const smeTabByKey = (key) => SME_TABS.find((tab) => tab.key === key) || SME_TABS[0];

export const PLATFORMS = {
  NSE: 'NSE Emerge',
  BSE: 'BSE SME',
};

const exchangeOf = (text) => {
  if (/bse/i.test(text || '')) return 'BSE';
  if (/nse|emerge/i.test(text || '')) return 'NSE';
  return null;
};

// "Coreintegra Consulting Services Ltd. IPO" and "Coreintegra Consulting Services Limited" -> same key
export const matchKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/\b(limited|ltd|ipo|sme|nse|bse|emerge)\b\.?/g, '')
    .replace(/[^a-z0-9]/g, '');

const phaseFor = (openOn, closeOn, today) => {
  if (!openOn || today < openOn) return 'upcoming';
  if (closeOn && today > closeOn) return 'closed';
  if (closeOn === today) return 'closing_today';
  return 'open';
};

const positive = (value) => {
  const number = Number(value);
  return value !== null && value !== '' && Number.isFinite(number) && number > 0 ? number : null;
};

const bandText = (low, high) => {
  if (!high) return null;
  return low && low !== high ? `₹${low} – ₹${high}` : `₹${high}`;
};

const fromNse = (ipo, today) => {
  const { low, high } = parsePriceRange(ipo.issuePrice || ipo.priceBand);
  const openOn = toIsoDate(ipo.issueStartDate);
  const closeOn = toIsoDate(ipo.issueEndDate);
  const shares = positive(ipo.issueSize);
  return {
    company: String(ipo.companyName || ipo.symbol).trim(),
    symbol: ipo.symbol || '',
    exchange: 'NSE',
    status: phaseFor(openOn, closeOn, today),
    openOn,
    closeOn,
    priceBand: bandText(low, high),
    priceHigh: high,
    lotSize: positive(ipo.lotSize),
    sizeCr: shares && high ? Math.round(((shares * high) / 1e7) * 100) / 100 : null,
  };
};

// gmpRows are utils/gmpBoard.js board rows (selectGmpBoardRows)
const fromGmp = (row) => ({
  company: row.name,
  slug: row.slug,
  exchange: exchangeOf(row.exchange),
  status: row.status === 'other' ? null : row.status,
  openOn: row.openOn,
  closeOn: row.closeOn,
  allotmentOn: row.boaOn,
  listingOn: row.listingOn,
  priceBand: bandText(null, row.price),
  priceHigh: row.price,
  lotSize: row.lotSize,
  sizeCr: row.sizeCr,
  gmp: row.hasGmp ? row.gmp : null,
  gmpPercent: row.hasGmp ? row.gmpPercent : null,
  estListing: row.estListing,
  subscription: row.subscription,
});

const pick = (...values) => values.find((value) => value !== null && value !== undefined);

// The same issue is often named differently across sources ("Core Integra
// Consulting" vs "Coreintegra Consulting Services Limited"): treat two names as
// one issue when one key is a prefix of the other, unless their opening dates
// disagree.
const MIN_PREFIX = 8;

// Two company names for the same issuer, by the same prefix rule
export const isSameCompany = (nameA, nameB) => {
  const [a, b] = [matchKey(nameA), matchKey(nameB)];
  if (!a || !b) return false;
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  return shorter.length >= MIN_PREFIX && longer.startsWith(shorter);
};

const sameIssue = (entry, key, openOn) => {
  if (entry.key === key) return true;
  const [shorter, longer] = entry.key.length <= key.length ? [entry.key, key] : [key, entry.key];
  if (shorter.length < MIN_PREFIX || !longer.startsWith(shorter)) return false;
  return !entry.openOn || !openOn || entry.openOn === openOn;
};

/**
 * @param {object} feeds
 * @param {object[]} feeds.smeList - GET /api/core/ipos/sme -> data (raw items)
 * @param {object[]} feeds.liveIpos - NSE rows (GET /api/market/ipos/sme/upcoming items); non-SME rows are ignored
 * @param {object[]} feeds.gmpRows - GMP board rows (selectSmeGmpBoardRows / selectGmpBoardRows); non-SME rows are ignored
 * @param {object[]} feeds.subscriptionRows - subscription rows by symbol (selectSmeSubscriptionRows)
 * @param {string} today - local ISO date, injectable for tests
 */
export const buildSmeIpoRows = ({ smeList = [], liveIpos = [], gmpRows = [], subscriptionRows = [] }, today = todayIso()) => {
  const entries = [];
  const add = (source, row) => {
    const key = matchKey(row.company);
    if (!key) return;
    const entry = entries.find((candidate) => sameIssue(candidate, key, row.openOn));
    if (entry) {
      if (!entry[source]) entry[source] = row;
      if (!entry.openOn) entry.openOn = row.openOn;
    } else {
      entries.push({ key, openOn: row.openOn, [source]: row });
    }
  };

  // The SME list comes in the GMP item shape (company_name, open_date "28-Sep", ...)
  // minus the board, so it goes through the same board-row mapping
  const now = new Date(`${today}T12:00:00`);
  (Array.isArray(smeList) ? smeList : [])
    .filter((item) => item?.company_name)
    .forEach((item) => {
      const [row] = mapApiItemsToBoardRows([{ ...item, board: 'SME' }], now);
      add('core', { ...fromGmp(row), coreId: item.id ?? null });
    });

  liveIpos
    .filter((ipo) => ipo && (ipo.companyName || ipo.symbol) && deriveBoard(ipo.series) === 'sme')
    .forEach((ipo) => add('nse', fromNse(ipo, today)));

  gmpRows.filter((row) => row && row.board === 'SME').forEach((row) => add('gmp', fromGmp(row)));

  const subscriptionBySymbol = new Map(
    subscriptionRows.filter((row) => row.symbol && typeof row.total === 'number').map((row) => [row.symbol, row.total])
  );

  return entries
    .map(({ key, core, nse, gmp }) => {
      const base = core || nse || gmp;
      // NSE's own dates decide open/upcoming; otherwise the SME list's status.
      // Once closed, the list or GMP source knows whether allotment or listing happened.
      const listed = [core?.status, gmp?.status].find((value) => ['allotted', 'listed'].includes(value));
      let status = nse ? nse.status : pick(core?.status, gmp?.status);
      if (status === 'closed' && listed) status = listed;
      if (!status) status = phaseFor(base.openOn, base.closeOn, today);

      const priceHigh = pick(nse?.priceHigh, core?.priceHigh, gmp?.priceHigh);
      const lotSize = pick(nse?.lotSize, core?.lotSize, gmp?.lotSize);
      const gmpValue = pick(gmp?.gmp, core?.gmp);
      const liveSubscription = nse?.symbol ? subscriptionBySymbol.get(nse.symbol) : undefined;
      const slug = gmp?.slug || core?.slug || toGmpSlug(base.company);

      return {
        id: nse?.symbol || (core?.coreId ? `ipo-${core.coreId}` : key),
        company: base.company,
        symbol: nse?.symbol || '',
        coreId: core?.coreId ?? null,
        exchange: pick(core?.exchange, nse?.exchange, gmp?.exchange) || null,
        status,
        openOn: pick(nse?.openOn, core?.openOn, gmp?.openOn),
        closeOn: pick(nse?.closeOn, core?.closeOn, gmp?.closeOn),
        allotmentOn: pick(gmp?.allotmentOn, core?.allotmentOn) || null,
        listingOn: pick(core?.listingOn, gmp?.listingOn) || null,
        priceBand: pick(nse?.priceBand, core?.priceBand, gmp?.priceBand) || null,
        priceHigh: priceHigh ?? null,
        lotSize: lotSize ?? null,
        lotValue: lotSize && priceHigh ? lotSize * priceHigh : null,
        sizeCr: pick(core?.sizeCr, nse?.sizeCr, gmp?.sizeCr) ?? null,
        gmp: gmpValue ?? null,
        gmpPercent: pick(gmp?.gmpPercent, core?.gmpPercent) ?? null,
        // From the upper band shown on the row, so the estimate matches it
        estListing: typeof gmpValue === 'number' && priceHigh ? priceHigh + gmpValue : null,
        subscription: pick(liveSubscription, gmp?.subscription) ?? null,
        gmpSlug: slug,
        // The catalog page when this issue has a record, the grey-market page
        // otherwise: only a couple of the SME list rows carry a catalog id
        detailPath: core?.coreId ? `/smeipo/${core.coreId}` : `/ipo-gmp/${slug}`,
      };
    })
    .sort(compareSmeRows);
};

// Open issues first (closing soonest), then upcoming by opening date, then
// closed/allotted/listed with the most recent first
const STATUS_RANK = { closing_today: 0, open: 1, upcoming: 2, closed: 3, allotted: 3, listed: 3 };

export const compareSmeRows = (a, b) => {
  const rank = (STATUS_RANK[a.status] ?? 4) - (STATUS_RANK[b.status] ?? 4);
  if (rank) return rank;
  if (a.status === 'upcoming') return (a.openOn || '9999').localeCompare(b.openOn || '9999') || a.company.localeCompare(b.company);
  if (CLOSED_STATUSES.includes(a.status)) return (b.closeOn || '').localeCompare(a.closeOn || '');
  return (a.closeOn || '9999').localeCompare(b.closeOn || '9999') || a.company.localeCompare(b.company);
};

export const filterSmeIpos = (rows, { tab = 'all', exchange = 'all', query = '' } = {}) => {
  const needle = query.trim().toLowerCase();
  return rows
    .filter(smeTabByKey(tab).test)
    .filter((row) => exchange === 'all' || row.exchange === exchange)
    .filter((row) => !needle || `${row.company} ${row.symbol}`.toLowerCase().includes(needle));
};

export const computeSmeStats = (rows) => {
  const open = rows.filter(SME_TABS[1].test);
  const upcoming = rows.filter(SME_TABS[2].test).sort((a, b) => (a.openOn || '9999').localeCompare(b.openOn || '9999'));
  const withGmp = rows.filter((row) => typeof row.gmpPercent === 'number' && !CLOSED_STATUSES.includes(row.status));
  return {
    total: rows.length,
    nse: rows.filter((row) => row.exchange === 'NSE').length,
    bse: rows.filter((row) => row.exchange === 'BSE').length,
    open,
    closingToday: rows.filter((row) => row.status === 'closing_today'),
    upcomingCount: upcoming.length,
    nextOpening: upcoming[0] || null,
    topGmp: withGmp.reduce((best, row) => (!best || row.gmpPercent > best.gmpPercent ? row : best), null),
  };
};

const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildSmeCsv = (rows) => {
  const header = [
    'Company', 'Symbol', 'Platform', 'Status', 'Open Date', 'Close Date', 'Allotment Date', 'Listing Date', 'Price Band',
    'Lot Size', 'Lot Value (INR)', 'Issue Size (INR Cr)', 'GMP (INR)', 'GMP (%)', 'Est. Listing (INR)', 'Subscription (x)',
  ];
  const lines = rows.map((row) =>
    [
      row.company,
      row.symbol,
      PLATFORMS[row.exchange],
      SME_STATUS[row.status]?.label,
      row.openOn,
      row.closeOn,
      row.allotmentOn,
      row.listingOn,
      row.priceBand,
      row.lotSize,
      row.lotValue,
      row.sizeCr,
      row.gmp,
      typeof row.gmpPercent === 'number' ? row.gmpPercent.toFixed(2) : null,
      row.estListing,
      typeof row.subscription === 'number' ? row.subscription.toFixed(2) : null,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...lines].join('\r\n');
};
